import { cn } from '@suscri/ui';
import type { LucideIcon } from 'lucide-react';
import type { ReactNode } from 'react';

interface KpiCardProps {
  label: ReactNode;
  value: ReactNode;
  helper: ReactNode;
  icon: LucideIcon;
  accent?: 'primary' | 'secondary' | 'info' | 'warning';
  badge?: ReactNode;
}

const accentClasses = {
  primary: 'bg-primary/15 text-primary ring-primary/20',
  secondary: 'bg-secondary/15 text-secondary ring-secondary/20',
  info: 'bg-info/15 text-info ring-info/20',
  warning: 'bg-warning/15 text-warning ring-warning/20',
};

export function KpiCard({
  label,
  value,
  helper,
  icon: Icon,
  accent = 'primary',
  badge,
}: KpiCardProps) {
  return (
    <article className="surface-card-interactive relative overflow-hidden p-5 sm:p-6">
      <div className="pointer-events-none absolute -right-12 -top-12 h-32 w-32 rounded-full bg-primary/10 blur-3xl" />
      <div className="relative flex items-start justify-between gap-4">
        <div className="min-w-0">
          <p className="text-sm font-medium text-muted-foreground">{label}</p>
          <p className="mt-3 text-3xl font-bold tracking-[-0.035em] text-foreground">{value}</p>
        </div>
        <span
          className={cn(
            'flex h-11 w-11 shrink-0 items-center justify-center rounded-xl ring-1',
            accentClasses[accent]
          )}
        >
          <Icon className="h-5 w-5" aria-hidden="true" />
        </span>
      </div>
      <div className="relative mt-4 flex items-center justify-between gap-3 border-t border-border/70 pt-4">
        <p className="text-xs leading-5 text-muted-foreground">{helper}</p>
        {badge && <span className="status-badge shrink-0 border-primary/20 bg-primary/10 text-primary">{badge}</span>}
      </div>
    </article>
  );
}
